import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "../../general/components/layout/SiteFooter";
import useRevealOnScroll from "../../general/components/common/useRevealOnScroll";
import { routes } from "../../general/routes/newsApi";
import FispTvNavbar from "../components/FispTvNavbar";
import {
    buildCategoryPath,
    fetchCategoryPreviewNews,
    fetchHomeNews,
    getHighlightedNews,
    normalizeNewsItem,
} from "../data/noticias";

const homeSections = [
    { slug: "desde-adentro", label: "Desde Adentro", aliases: ["Desde Adentro"] },
    { slug: "segunda-opinion", label: "Segunda Opinión", aliases: ["Segunda Opinión", "Segunda Opinion", "2da Opinión", "2da Opinion"] },
    { slug: "el-nudo", label: "El Nudo", aliases: ["El Nudo"] },
    { slug: "inspirando-el-cambio", label: "Inspirando el Cambio", aliases: ["Inspirando el Cambio", "Inspirando el cambio"] },
];

function buildHomeSectionState() {
    return homeSections.map((section) => ({ ...section, items: [], status: "pending" }));
}

function getItemKey(item) {
    return item?.slug || item?.id || item?.href || item?.title;
}

function NewsLink({ item, className, children }) {
    if (item.external) {
        return (
            <a href={item.href} target="_blank" rel="noreferrer" className={className}>
                {children}
            </a>
        );
    }

    return (
        <Link to={item.href} className={className}>
            {children}
        </Link>
    );
}

function formatMeta(item) {
    if (item.publishedAt) {
        return new Date(item.publishedAt).toLocaleDateString("es-AR");
    }

    return item.category;
}

function getHomeNewsImage(item) {
    if (Number(item?.homeOrder) === 1 && item?.coverImage) {
        return item.coverImage;
    }

    return item?.image || "";
}

function shuffleItems(items = []) {
    const nextItems = [...items];

    for (let index = nextItems.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        const currentItem = nextItems[index];
        nextItems[index] = nextItems[randomIndex];
        nextItems[randomIndex] = currentItem;
    }

    return nextItems;
}

function buildSecondaryNews(highlightedNews, allNews) {
    const featuredSlug = highlightedNews[0]?.slug;
    const byId = new Map();

    [...highlightedNews.slice(1), ...allNews]
        .filter((item) => item.slug !== featuredSlug)
        .forEach((item) => {
            const key = getItemKey(item);

            if (!byId.has(key)) {
                byId.set(key, item);
            }
        });

    return Array.from(byId.values()).slice(0, 9);
}

function FispTvState({ title, description, className = "" }) {
    return (
        <div className={`tv-state-card ${className}`.trim()}>
            <h2>{title}</h2>
            <p>{description}</p>
        </div>
    );
}

function FispTvLoadingMain() {
    return (
        <div className="tv-loading" role="status" aria-live="polite">
            <span className="tv-loading-announcer">Cargando FISP TV</span>

            <div className="main tv-loading-main" id="ultimas">
                <div className="content">
                    <div className="hero-note tv-loading-hero">
                        <div className="tv-skeleton tv-loading-hero-media"></div>
                        <div className="tv-loading-hero-copy">
                            <div className="tv-skeleton tv-skeleton-pill"></div>
                            <div className="tv-skeleton tv-skeleton-line is-xl"></div>
                            <div className="tv-skeleton tv-skeleton-line is-lg"></div>
                            <div className="tv-skeleton tv-skeleton-line is-md"></div>
                        </div>
                    </div>

                    {[0, 1, 2, 3].map((item) => (
                        <div key={item} className="tv-loading-note">
                            <div className="tv-skeleton tv-loading-note-thumb"></div>
                            <div className="tv-loading-note-copy">
                                <div className="tv-skeleton tv-skeleton-pill is-short"></div>
                                <div className="tv-skeleton tv-skeleton-line is-lg"></div>
                                <div className="tv-skeleton tv-skeleton-line is-md"></div>
                                <div className="tv-skeleton tv-skeleton-line is-sm"></div>
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="sidebar">
                    <div className="sb-block">
                        <div className="tv-skeleton tv-loading-heading"></div>

                        {Array.from({ length: 6 }, (_, item) => item).map((item) => (
                            <div key={item} className="tv-loading-sidebar-item">
                                <div className="tv-loading-sidebar-copy">
                                    <div className="tv-skeleton tv-skeleton-line is-sidebar-lg"></div>
                                    <div className="tv-skeleton tv-skeleton-line is-sidebar-sm"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
}

function FispTvLoadingMostViewed() {
    return (
        <div className="sb-block">
            <div className="sb-heading">Más vistos</div>

            {Array.from({ length: 6 }, (_, item) => item).map((item) => (
                <div key={item} className="tv-loading-sidebar-item">
                    <div className="tv-loading-sidebar-copy">
                        <div className="tv-skeleton tv-skeleton-line is-sidebar-lg"></div>
                        <div className="tv-skeleton tv-skeleton-line is-sidebar-sm"></div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function FispTvLoadingSection({ section }) {
    return (
        <div className="section-block tv-loading-section">
            <div className="section-inner">
                <div className="section-head">
                    <h2>{section.label}</h2>
                </div>

                <div className="section-grid">
                    {[0, 1, 2, 3].map((item) => (
                        <div key={`${section.slug}-${item}`} className="tv-loading-card">
                            <div className="tv-skeleton tv-loading-card-media"></div>
                            <div className="tv-loading-card-copy">
                                <div className="tv-skeleton tv-skeleton-line is-card-lg"></div>
                                <div className="tv-skeleton tv-skeleton-line is-card-sm"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

async function fetchMostViewedNewsStrict(signal, server) {
    const base = String(server || routes.Vercel).replace(/\/+$/, "");
    const response = await fetch(`${base}/noticias-mas-vistas`, { signal });

    if (!response.ok) {
        throw new Error("No se pudo obtener noticias mas vistas");
    }

    const result = await response.json();

    if (!Array.isArray(result)) {
        return [];
    }

    return result
        .filter((item) => item && item.titulo)
        .map((item) => normalizeNewsItem(item, server));
}

export default function FispTv() {
    const pageRef = useRef(null);
    const [server, setServer] = useState(routes.Vercel);
    const [mainNews, setMainNews] = useState([]);
    const [mostViewedNews, setMostViewedNews] = useState([]);
    const [sections, setSections] = useState(buildHomeSectionState());
    const [mainStatus, setMainStatus] = useState("loading");
    const [mostViewedStatus, setMostViewedStatus] = useState("pending");

    useRevealOnScroll(pageRef);

    useEffect(() => {
        let active = true;

        fetch(`${routes.Hostinger}/ping`)
            .then(async (response) => {
                const data = await response.text();

                if (!active) {
                    return;
                }

                if (data === "pong") {
                    setServer(routes.Hostinger);
                } else {
                    setServer(routes.Vercel);
                }
            })
            .catch(() => {
                if (active) {
                    setServer(routes.Vercel);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;

        async function loadNews() {
            setMainStatus("loading");
            setMainNews([]);
            setMostViewedStatus("pending");
            setMostViewedNews([]);
            setSections(buildHomeSectionState());
            let viewedNewsResult = [];
            const loadedSectionItems = [];

            try {
                const homeNews = await fetchHomeNews(controller.signal, server);

                if (!active) {
                    return;
                }

                setMainNews(homeNews);
                setMainStatus("ready");
            } catch (error) {
                if (error?.name === "AbortError") {
                    return;
                }

                if (active) {
                    setMainNews([]);
                    setMainStatus("error");
                }
            }

            if (!active) {
                return;
            }

            setMostViewedStatus("loading");

            try {
                const viewedNews = await fetchMostViewedNewsStrict(controller.signal, server);

                if (!active) {
                    return;
                }

                viewedNewsResult = viewedNews;
                setMostViewedNews(viewedNews);
                setMostViewedStatus("ready");
            } catch (error) {
                if (error?.name === "AbortError") {
                    return;
                }

                if (active) {
                    setMostViewedNews([]);
                    setMostViewedStatus("error");
                }
            }

            for (const section of homeSections) {
                if (!active) {
                    return;
                }

                setSections((currentSections) =>
                    currentSections.map((currentSection) =>
                        currentSection.slug === section.slug
                            ? { ...currentSection, status: "loading", items: [] }
                            : currentSection,
                    ),
                );

                try {
                    const items = await fetchCategoryPreviewNews(section.slug, controller.signal, server);

                    if (!active) {
                        return;
                    }

                    loadedSectionItems.push(...items.slice(0, 4));
                    setSections((currentSections) =>
                        currentSections.map((currentSection) =>
                            currentSection.slug === section.slug
                                ? { ...currentSection, status: "ready", items: items.slice(0, 4) }
                                : currentSection,
                        ),
                    );
                } catch (error) {
                    if (error?.name === "AbortError") {
                        return;
                    }

                    if (active) {
                        setSections((currentSections) =>
                            currentSections.map((currentSection) =>
                                currentSection.slug === section.slug
                                    ? { ...currentSection, status: "error", items: [] }
                                    : currentSection,
                            ),
                        );
                    }
                }
            }

            if (!active || !viewedNewsResult.length || viewedNewsResult.length >= 10) {
                return;
            }

            const currentKeys = new Set(viewedNewsResult.map((item) => getItemKey(item)));
            const uniqueCandidates = [];
            const seenCandidateKeys = new Set();

            loadedSectionItems.forEach((item) => {
                const itemKey = getItemKey(item);

                if (!itemKey || currentKeys.has(itemKey) || seenCandidateKeys.has(itemKey)) {
                    return;
                }

                seenCandidateKeys.add(itemKey);
                uniqueCandidates.push(item);
            });

            if (!uniqueCandidates.length) {
                return;
            }

            const extraItems = shuffleItems(uniqueCandidates).slice(0, Math.max(0, 10 - viewedNewsResult.length));

            if (!extraItems.length) {
                return;
            }

            setMostViewedNews([...viewedNewsResult, ...extraItems]);
        }

        loadNews();

        return () => {
            active = false;
            controller.abort();
        };
    }, [server]);

    const highlightedNews = getHighlightedNews(mainNews);
    const featuredNews = highlightedNews[0] || mainNews[0] || null;
    const secondaryNews = buildSecondaryNews(highlightedNews, mainNews);
    const showContent = mainStatus === "ready" && Boolean(featuredNews);
    const showEmptyState = mainStatus === "ready" && !mainNews.length;
    const featuredNewsImage = featuredNews ? getHomeNewsImage(featuredNews) : "";
    const shouldRenderMain = mainStatus !== "loading";
    const shouldRenderFooter = mainStatus !== "loading";

    return (
        <div className="fisp-tv fisp-tv-home" ref={pageRef}>
            <FispTvNavbar activeCategory="Inicio" />

            {mainStatus === "loading" ? <FispTvLoadingMain /> : null}

            {shouldRenderMain ? (
                <>
                    <div className="main" id="ultimas">
                        <div className="content">
                            {showContent ? (
                                <>
                                    <NewsLink item={featuredNews} className="hero-note">
                                        {featuredNewsImage ? <img src={featuredNewsImage} alt={featuredNews.title} /> : <div className="hero-note-fallback bg1"></div>}
                                        <div className="overlay"></div>
                                        <div className="info">
                                            <div className="cat">{featuredNews.category} - Destacado</div>
                                            <h2>{featuredNews.title}</h2>
                                            <p className="desc">{featuredNews.description || "Contenido disponible en FISP TV."}</p>
                                            <span className="desc-meta">{formatMeta(featuredNews)}</span>
                                        </div>
                                    </NewsLink>

                                    {secondaryNews.map((item, index) => {
                                        const itemImage = getHomeNewsImage(item);

                                        return (
                                            <NewsLink key={getItemKey(item) || `${item.title}-${index}`} item={item} className="note">
                                                <div className="note-img">
                                                    {itemImage ? <img src={itemImage} alt={item.title} /> : <div className={`ph bg${(index % 10) + 1}`}></div>}
                                                </div>
                                                <div className="note-body">
                                                    <span className="cat">{item.category}</span>
                                                    <h3>{item.title}</h3>
                                                    <p>{item.description || "Contenido disponible en FISP TV."}</p>
                                                    <span className="meta">{formatMeta(item)}</span>
                                                </div>
                                            </NewsLink>
                                        );
                                    })}
                                </>
                            ) : null}

                            {mainStatus === "error" ? (
                                <FispTvState
                                    className="is-inline"
                                    title="No se pudo cargar FISP TV"
                                    description="Hubo un problema consultando las noticias principales. Proba nuevamente en unos minutos."
                                />
                            ) : null}

                            {showEmptyState ? (
                                <FispTvState
                                    className="is-inline"
                                    title="Todavia no hay contenidos publicados"
                                    description="Cuando se publiquen noticias en la base van a aparecer automaticamente aca."
                                />
                            ) : null}
                        </div>

                        <aside className="sidebar">
                            {mostViewedStatus === "loading" ? <FispTvLoadingMostViewed /> : null}

                            {mostViewedStatus === "error" ? (
                                <div className="sb-block">
                                    <div className="sb-heading">Más vistos</div>
                                    <FispTvState
                                        className="is-sidebar"
                                        title="No se pudo cargar"
                                        description="Hubo un problema consultando esta lista."
                                    />
                                </div>
                            ) : null}

                            {mostViewedStatus === "ready" && mostViewedNews.length ? (
                                <div className="sb-block">
                                    <div className="sb-heading">Más vistos</div>

                                    {mostViewedNews.map((item, index) => (
                                        <NewsLink key={getItemKey(item) || `${item.title}-${index}`} item={item} className="sb-note">
                                            <span className="sb-rank">{index + 1}</span>
                                            <div className="sb-note-body">
                                                <h4>{item.title}</h4>
                                                <div className="cat">{item.category}</div>
                                            </div>
                                        </NewsLink>
                                    ))}
                                </div>
                            ) : null}

                            {mostViewedStatus === "ready" && !mostViewedNews.length ? (
                                <div className="sb-block">
                                    <div className="sb-heading">Más vistos</div>
                                    <FispTvState
                                        className="is-sidebar"
                                        title="Sin noticias"
                                        description="Todavia no hay contenidos para mostrar aca."
                                    />
                                </div>
                            ) : null}
                        </aside>
                    </div>

                    {sections.map((section) => {
                        if (section.status === "pending") {
                            return null;
                        }

                        if (section.status === "loading") {
                            return <FispTvLoadingSection key={section.slug} section={section} />;
                        }

                        if (section.status === "error") {
                            return (
                                <div key={section.slug} className="section-block" id={section.slug}>
                                    <div className="section-inner">
                                        <div className="section-head">
                                            <h2>{section.label}</h2>
                                        </div>

                                        <div className="section-grid">
                                            <FispTvState
                                                className="is-inline is-section"
                                                title="No se pudo cargar esta sección"
                                                description="Hubo un problema al consultar estas notas."
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        if (!section.items.length) {
                            return (
                                <div key={section.slug} className="section-block" id={section.slug}>
                                    <div className="section-inner">
                                        <div className="section-head">
                                            <h2>{section.label}</h2>
                                        </div>

                                        <div className="section-grid">
                                            <FispTvState
                                                className="is-inline is-section"
                                                title="Sin notas todavía"
                                                description="Cuando haya contenidos publicados, van a aparecer aca."
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={section.slug} className="section-block" id={section.slug}>
                                <div className="section-inner">
                                    <div className="section-head">
                                        <h2>{section.label}</h2>
                                        <Link to={buildCategoryPath(section)}>
                                            Ver más
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M5 12h14M12 5l7 7-7 7"></path>
                                            </svg>
                                        </Link>
                                    </div>
                                    <div className="section-grid">
                                        {section.items.map((item, itemIndex) => (
                                            <NewsLink key={getItemKey(item) || `${item.title}-${itemIndex}`} item={item} className="card">
                                                <div className="card-img">
                                                    {item.image ? <img src={item.image} alt={item.title} /> : <div className={`ph bg${(itemIndex % 10) + 1}`}></div>}
                                                </div>
                                                <div className="card-body">
                                                    <h4>{item.title}</h4>
                                                    <p>{formatMeta(item)}</p>
                                                </div>
                                            </NewsLink>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <section className="tv-home-directory-cta reveal">
                        <div className="tv-home-directory-cta-inner">
                            <div className="tv-home-directory-cta-copy">
                                <span>Explorá FISP TV</span>
                                <h2>Ver todos los segmentos</h2>
                                <p>Accedé al mapa completo de entrevistas, innovación, editoriales, bienestar y contenidos temáticos.</p>
                            </div>

                            <Link to="/fisp-tv/segmentos" className="tv-home-directory-cta-link">
                                Ir a segmentos
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
                                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                                </svg>
                            </Link>
                        </div>
                    </section>
                </>
            ) : null}

            {shouldRenderFooter ? (
                <SiteFooter variant="tv" description="Multimedio de salud digital con entrevistas, informes, podcasts y coberturas especiales." />
            ) : null}
        </div>
    );
}
